/** @module IglooTimer **/

/**
 * Exports the IglooTimer public functions
 * @param {Igloo} igloo The igloo module
 * @return {Object} The public functions for the class:
 * [addUnit]{@link module:IglooTimer~add_unit},
 * [removeUnit]{@link module:IglooTimer~remove_unit},
 * [registerFunction]{@link module:IglooTimer~register_function}, and
 * [startNewAd]{@link module:IglooTimer~start_new_ad}
 */
module.exports = function(igloo) {
  'use strict';

  /**** CONSTANTS ****/
  /**
   * The default length of the ad stack before switching to visible only mode
   * @type {Number}
   */
  var stack_length = 180000;
  /**
   * The auction timeout
   * @type {Number}
   */
  var auction_timeout = 1000;
  /**
   * Igloo Logger
   * @type {module:IglooLogger}
   */
  var log = require('./IglooLogger')(igloo, 'PREDICT Timer');
  /**
   * The igloo units currently being timed
   * @type {Object}
   */
  var igloo_units = {};
  /**
   * The maximum number of ads to adjust the time down on for each MRC session
   * @type {Number}
   */
  var max_mrc_adjusted = 8;
  /**
   * The last seen igloo debug state
   * @type {String|Boolean}
   */
  var debug_state = igloo.debug;
  /**
   * The functions to run every 100ms
   * @type {Array}
   */
  var registered_functions = [];
  /**
   * The ID of the setInterval used for the timing function
   * @type {Integer}
   */
  var interval_id;
  /*
   * The current URL. Used to determine pageview events on a single page web app
   * @type {String}
   */
  var old_url = igloo.top.location.href;

  /**** TIMER CONTROL FUNCTIONS ****/

  /**
   * This function updates the timers for each ad unit and requests auctions as
   * needed.
   * @param {Object} units The igloo_units to parse and check
   */
  function update_unit_timers(units) {
    // Loop through the timers
    var current_unit;
    var current_time = (new Date()).getTime();
    var elapsed_time;
    var remaining_time;
    for (var name in units) {
      // Check for needing to delete the unit
      if (typeof units[name].product_zone === 'undefined' || units[name].product_zone.getBoundingClientRect().height === 0) {
        if (units[name].enable === true) {
          log.info('Ad Unit Lost', name);
          units[name].enable = false;
        }
        continue;
      } else if (units[name].enable === false) {
        log.info('Ad Unit Found', name);
        units[name].enable = true;
      }

      // Save out the current variable
      current_unit = units[name];

      // Skip if already paused
      if (should_unit_pause(current_unit, current_time)) {
        pause_timer(current_unit, current_time);
        continue;
      }

      // Call restart_timer to perform some math
      restart_timer(current_unit, current_time);

      // Set the debugging divs
      elapsed_time = (Math.round((current_time - current_unit.timing.started) / 100) / 10);
      remaining_time = Math.round((current_unit.timing.length / 100) - (elapsed_time * 10)) / 10;
      if ([true, 'v', 'vv'].indexOf(igloo.debug) !== -1) {
        current_unit.debug_divs.te.innerHTML = elapsed_time + 's';
        current_unit.debug_divs.tr.innerHTML = remaining_time + 's';
      }

      // Check for needing an auction
      if (!current_unit.bid_requested && remaining_time <= ((2 * auction_timeout) + 500) / 1000) {
        // Mark as having requested a bid
        current_unit.bid_requested = true;

        // Log that a request has been made
        log.info(current_unit.name, 'Bid Needed');

        // Add the needed bid
        igloo.auctioneer.requestBid(current_unit.name);
      }
    }
  } // --> update_unit_timers

  /**
   * Determines if the passed unit should pause
   * @param  {Object}  current_unit The current unit object
   * @param  {Integer} current_time The current timestamp in milliseconds
   * @return {Boolean}              True if the unit should pause else false
   */
  function should_unit_pause(current_unit, current_time) {
    if (current_unit.pause.mouse) {
      // Mouse is over the div
      return true;
    } else if (current_unit.pause.norotate && current_unit.timing.length) {

      // find corresponding ad unit
      var ad_unit = null;
      for(var i = 0; i < igloo.ad_zones.length; i++) {
        if (igloo.ad_zones[i].code === current_unit.name) {
          ad_unit = igloo.ad_zones[i];
          break;
        }
      }

      // if there was an errored bid, do not pause
      if (ad_unit && ad_unit.getTopBid().error === true) {
        return false;
      }

      // Norotate flag and already displayed an ad
      return true;
    } else if (igloo.pause) {
      // Global pause flag
      return true;
    } else if (
      typeof current_unit.visibility[current_unit.visible_metric] !== 'undefined' &&
      !current_unit.visibility[current_unit.visible_metric] &&
      current_time - current_unit.started_at >= current_unit.timing.stack_length &&
      current_unit.timing.length !== 0
    ) {
      // Visible mode and unit not visible
      return true;
    } else if (typeof current_unit.win['ig_rotation_control_' + current_unit.name] !== 'undefined' && current_unit.win['ig_rotation_control_' + current_unit.name] === false) {
      // Centipede flag is set to false
      return true;
    }

    return false;
  } // --> should_unit_pause

  /**
   * This function pauses the given unit at the given time
   * @param  {Object} unit         The unit to pause
   * @param  {Number} current_time The timestamp to pause the unit at
   */
  function pause_timer(unit, current_time) {
    // Exit if already paused
    if (unit.pause.paused) {
      return true;
    }

    // Handle the debugging divs
    var strings = get_pause_message(unit, current_time);
    unit.debug_divs.pa.innerHTML = strings.debug;
    log.verbose(unit.name + ':', 'Paused:', strings.reason);

    // Check for centipede flag to end the ad if more than 75% of the ad time has passed
    if (strings.debug === 'CENT' && (current_time - unit.timing.started) >= (unit.timing.length * 0.75)) {
      unit.timing.length = current_time - unit.timing.started;
    }

    // Add the paused at time and switch the flag
    unit.pause.paused_at = current_time;
    unit.pause.paused = true;
    return true;
  } // --> pause_timer

  /**
   * This function pauses the given unit at the given time
   * @param  {Object} unit         The unit to get the pause string for
   * @param  {Number} current_time The timestamp to pause the unit at
   * @return {Object}              A debug and reason string
   */
  function get_pause_message(unit, current_time) {
    var debug_string = 'PAUSED';
    var reason = 'Unknown Reason';
    if (unit.pause.norotate && unit.timing.length) {
      debug_string = 'DNR';
      reason = 'Static Ad';
    } else if (unit.pause.mouse) {
      // Paused because of mouseover
      debug_string = 'MOUSE';
      reason = 'Mouseover';
    } else if (igloo.pause) {
      // Globally paused
      debug_string = 'GLOBAL';
      reason = 'Global Pause';
    } else if (
      typeof unit.visibility[unit.visible_metric] !== 'undefined' &&
      !unit.visibility[unit.visible_metric] &&
      current_time - unit.started_at >= unit.timing.stack_length &&
      unit.timing.length !== 0
    ) {
      // Paused because out of view
      debug_string = 'VIS';
      reason = 'Visibility Mode';
    } else if (typeof unit.win['ig_rotation_control_' + unit.name] !== 'undefined' && unit.win['ig_rotation_control_' + unit.name] === false) {
      // Paused because of the control variable (centipede)
      debug_string = 'CENT';
      reason = 'Centipede Pause';
    }

    return {
      debug: debug_string,
      reason: reason,
    };
  } // --> get_pause_message

  /**
   * This function restarts the timer
   * @param  {Object} unit         The unit to restart
   * @param  {Number} current_time The timestamp to restart the unit at
   */
  function restart_timer(unit, current_time) {
    // Exit if the timer isn't paused
    if (!unit.pause.paused) {
      return true;
    }

    // Log the restart
    log.verbose(unit.name + ':', 'Started');

    // Handle debugging divs
    unit.debug_divs.pa.innerHTML = 'RUN';

    // Figure out the math of starting time (if needed)
    if (current_time - unit.timing.started > unit.timing.length - 5000 && unit.timing.length >= 5000) {
      // If the add is past 5s until rotation, add some time to the timer
      unit.timing.started = current_time + 5000 - unit.timing.length;
    }

    // Set the timer as unpaused
    unit.pause.paused = false;
    unit.pause.paused_at = null;
  } // --> restart_timer

  /**** VISIBILITY CONTROL FUNCTIONS ****/

  /**
   * This function checks the visibility of all of the igloo units. This
   * function is run every 100ms.
   * @param {Object} units The local copy of igloo_units
   */
  function check_visibility(units) {
    // Determine if the tab is in focus
    var tab_in_focus = is_tab_in_focus();

    // Save the current time
    var current_time = (new Date()).getTime();

    // Check the individual visibility
    for (var name in units) {
      // Get the current unit
      var current_unit = units[name];

      // Check the product visibility
      if (
        !current_unit.prod_vis.was_visible &&
        igloo.utils.elementIsVisible(
          current_unit.product_zone, // Div to check
          null, // Div to display % in
          null, // Igloo debugging state
          0.5 // Ratio to consider visible
        )
      ) {
        // Set the flag
        current_unit.prod_vis.was_visible = true;

        // Call the callback
        current_unit.prod_vis.was_visible_callback();
      }

      // Check the visibility
      var current_unit_visible = igloo.utils.elementIsVisible(current_unit.ad_zone, current_unit.debug_divs.vi, igloo.debug, 0.5);

      // Set the easy visibility variables
      current_unit.visibility.is_visible_pixels = current_unit_visible;
      current_unit.visibility.is_visible_loose = current_unit_visible && tab_in_focus;

      // Check for MRC complient visibility
      var mrc_visible = false;
      if (current_unit.visibility.is_visible_loose && current_unit.visibility.visible_start !== null) {
        // The unit is visible and has been visible for some period

        // Check for MRC visible
        if (current_time - current_unit.visibility.visible_start >= 1000) {
          // Set the variable
          mrc_visible = true;
        }
      } else if (current_unit.visibility.is_visible_loose) {
        // Set the openx bid
        current_unit.openx.rv = 'on';

        // The unit is visible starting now so set the start time
        current_unit.visibility.visible_start = current_time;
      } else {
        // Unit is not visible and OpenX should not be called
        current_unit.openx.rv = 'off';
      }

      // Check against the saved value
      if (mrc_visible !== current_unit.visibility.is_visible) {
        // Do the console logging and change the debug div if needed
        update_visibility_marker(current_unit, mrc_visible);
      }
    }
  } // --> check_visibility

  /**
   * Determines if the current tab is in focus
   * @return {Boolean} Whether the tab is in focus or not
   */
  function is_tab_in_focus() {
    var tab_in_focus = false;
    var top_document = igloo.top.document;
    if (typeof top_document.hidden !== 'undefined') {
      // Use document.hidden
      tab_in_focus = !top_document.hidden;
    } else {
      // Check an array of prefixes
      var prefixes = ['webkit', 'moz', 'ms', 'o'];
      for (var i = 0; i < prefixes.length; i++) {
        // Check for the existence of the prefix
        if (typeof top_document[prefixes[i] + 'Hidden'] !== 'undefined') {
          tab_in_focus = !top_document[prefixes[i] + 'Hidden'];
        }
      }
    }

    return tab_in_focus;
  } // --> is_tab_in_focus

  /**
   * Updates the visibility div and verbose logs changes
   * @param  {Object}  current_unit The current unit object
   * @param  {boolean} mrc_visible  Whether the zone is MRC visible
   */
  function update_visibility_marker(current_unit, mrc_visible) {
    // Make the strings that will be used
    var debug_strings = {
      state: 'Not Visible',
      color: '#00f',
    };
    if (mrc_visible) {
      debug_strings = {
        state: 'Visible',
        color: '#f00',
      };
    }

    // Log the change
    log.verbose('MRC Visible ' + current_unit.name + ':', 'State Changed:', debug_strings.state);

    // Set the variable
    current_unit.visibility.is_visible = mrc_visible;

    // Set the color of the div
    igloo.utils.setElementAttributes(current_unit.debug_divs.vi, {
      style: {
        'background-color': debug_strings.color,
      }
    });

    if (
      mrc_visible && // Is visible
      current_unit.visibility.do_visibile_adjustment && // Adjustment allowed
      !current_unit.visibility.was_adjusted && // Hasn't already been adjusted
      current_unit.visibility.visible_count < max_mrc_adjusted && // Not past the adjustment limit
      current_unit.timing.length >= 20000 // More than 20s in the ad rotation
    ) {
      // If newly visible, possibly adjust the time
      log.info('MRC Visible ' + current_unit.name + ':', 'Timing Adjustment:', current_unit.visibility.visible_count + 1);
      current_unit.visibility.was_logged = true;

      // Adjust the time
      current_unit.timing.length *= 0.5;

      // Set the adjusted flag
      current_unit.visibility.was_adjusted = true;
    } else if (mrc_visible && !current_unit.visibility.was_logged) {
      // Log why the adjustment wasn't made
      current_unit.visibility.was_logged = true;

      // Figure out the reason
      var reason = get_noadjust_reason(current_unit);
      log.info('MRC Visible ' + current_unit.name + ':', 'No Adjustment:', reason);
    } else if (!mrc_visible) {
      // If not visible, reset some values

      // Reset the start time
      current_unit.visibility.visible_start = null;
    }
  } // --> update_visibility_marker

  /**
   * Get the reason that adjustment hasn't occured
   * @param  {Object} current_unit The current unit's object
   * @return {String}              The reason there wasn't any adjustment
   */
  function get_noadjust_reason(current_unit) {
    log.trace('get_noadjust_reason', arguments);

    if (!current_unit.visibility.do_visibile_adjustment) {
      return 'Adjustment Disabled';
    } else if (current_unit.visibility.visible_count >= max_mrc_adjusted) {
      return 'Adjustment Limit Reached';
    } else if (current_unit.timing.length < 20000) {
      return 'Timing Too Short';
    }

    return 'Unknown Reason';
  } // --> get_noadjust_reason

  /**** UTILITY FUNCTIONS ****/

  /**
   * This function runs all of the registered functions. This is executed every
   * 100ms and will run internal as well as external (to the module) functions
   */
  function timer_main() {
    // Loop through the registered functions
    for (var i = 0; i < registered_functions.length; i++) {
      registered_functions[i]();
    }
  } // --> timer_main

  /**
   * This function handles the mouse moving over the ad div
   * @param {Object}  units The local copy of igloo_units
   * @param {String}  name  The name of the unit that the mouse was moved over
   * @param {Boolean} pause Whether to pause or start the unit
   */
  function mouse_handler(units, name, pause) {
    log.trace('mouse_handler', arguments);

    // Set the pause variable
    units[name].pause.mouse = pause;
  } // --> mouse_handler

  /**
   * Checks the state of igloo.debug and, if its changed, calls the function
   * for each ad unit that changes its state
   */
  function debug_check() {
    // Check for a change in debug state
    if (igloo.debug === debug_state) {
      return false;
    }

    // Save the debug_state
    debug_state = igloo.debug;

    // Loop through the ad units
    for (var name in igloo_units) {
      // Call the function
      igloo_units[name].debug_callback();
    }
  } // --> debug_check

  /**
   * Checks for a changed URL indicating a new pageview
   */
  function url_check() {
    // Check the url
    if (old_url !== igloo.top.location.href) {
      // Log that the pageview event happened
      log.info('New Pageview');

      // Clear the pageview events
      igloo.tracker_called = [];

      // Save the new URL
      old_url = igloo.top.location.href;
    }
  } // --> url_check

  /**** COMMUNICATION FUNCTIONS ****/

  /**
   * Add a unit to be monitored via the timer
   * @param  {Object}     config      An array of the config for the zone:
   * @key    {Function}   callback   The callback function to run if the product
   *                                 is deemed to be visible
   * @key    {Function}   debug      The callback function to run if the debug
   *                                 state changes
   * @key    {Object}     debug_divs The debugging divs for the unit
   * @key    {DOMElement} iframe     The ad top iframe of the unit being added
   * @key    {Integer}    length     (Optional) The length of the load stack
   * @key    {String}     name       The name of the unit to add
   * @key    {DOMElement} product    The div that contains the entire product to
   *                                 determine visibility
   * @key    {Boolean}    rotate     Whether to rotate the ads
   * @key    {String}     visible    (Optional) Which visibility metric to use
   *                                 for the visible stack
   * @key    {Window}     win        The window that the igloo unit is in
   * @return {Object}                The object added to the array
   */
  function add_unit(config) {
    log.trace('add_unit', arguments);

    // Get the current time
    var started = (new Date()).getTime();

    // Create the empty array
    igloo_units[config.name] = {
      /**
       * Variables used to pause and restart the ad zone
       * @type {Object}
       */
      pause: {
        /**
         * Describes whether the mouse is over the ad unit
         * @type {Boolean}
         */
        mouse: false,
        /**
         * Whether the unit is a norotate unit
         * @type {Boolean}
         */
        norotate: !config.rotate,
        /**
         * Whether the ad unit is currently paused
         * @type {Boolean}
         */
        paused: false,
        /**
         * If paused, what time the unit was paused at
         * @type {Number}
         */
        paused_at: null,
      },
      /**
       * Used to control the time of the current ad
       * @type {Object}
       */
      timing: {
        /**
         * Length of the ad (in ms)
         * @type {Number}
         */
        length: 0,
        /**
         * What time the ad was started at
         * @type {Number}
         */
        started: started,
        /**
         * The amount of time to run the onload stack
         * @type {Integer}
         */
        stack_length: typeof config.length === 'number' ? config.length : stack_length,
      },
      /**
       * Variables that control MRC viewability code
       * @type {Object}
       */
      visibility: {
        /**
         * Switch to enable or disable halving the time if MRC viewable
         * @type {Boolean}
         */
        do_visibile_adjustment: false,
        /**
         * Whether the ad was adjusted for MRC viewability
         * @type {Boolean}
         */
        was_adjusted: false,
        /**
         * Whether the ad is currently MRC viewable
         * @type {Boolean}
         */
        is_visible: false,
        /**
         * Whether the ad is currently MRC viewable (ignoring time)
         * @type {Boolean}
         */
        is_visible_loose: false,
        /**
         * Whether the ad is currently viewable (ignoring time and tab focus)
         * @type {Boolean}
         */
        is_visible_pixels: false,
        /**
         * This is used to make units only rotate on interaction
         * @type {Boolean}
         */
        is_visible_interact: false,
        /**
         * Number of ads that have been MRC adjusted
         * @type {Number}
         */
        visible_count: 0,
        /**
         * The timestamp of the first positive visibility measurement
         * @type {Integer}
         */
        visible_start: null,
        /**
         * Boolean to determine whether to log why an adjustment was not made
         * @type {Boolean}
         */
        was_logged: false,
      },
      /**
       * Variables that control product visibility
       * @type {Object}
       */
      prod_vis: {
        /**
         * Determines if the product has been visible or not
         * @type {Boolean}
         */
        was_visible: false,
        /**
         * The function to call when the product has been visible
         * @type {Function}
         */
        was_visible_callback: config.callback,
      },
      /**
       * Whether to check the unit's timer
       * @type {Boolean}
       */
      enable: true,
      /**
       * Name of the ad unit
       * @type {String}
       */
      name: config.name,
      /**
       * Whether a bid request has already been sent
       * @type {Boolean}
       */
      bid_requested: false,
      /**
       * The iframe that the ads live in (for viewability measurement)
       * @type {DOMElement}
       */
      ad_zone: config.iframe,
      /**
       * The div that contains the entire product (for viewability measurement)
       * @type {DOMElement}
       */
      product_zone: config.product,
      /**
       * An array of the debugging divs
       * @type {Object}
       */
      debug_divs: config.debug_divs,
      /**
       * When the stack was started (for switching to the visibility stack)
       * @type {Number}
       */
      started_at: started,
      /**
       * The window the igloo unit is embedded in
       * @type {Window}
       */
      win: config.win,
      /**
       * Which visibility metric to use for the visible stack
       * @type {String}
       */
      visible_metric: typeof config.visible === 'string' ? config.visible : 'is_visible_pixels',
      /**
       * The OpenX bid that is used for visibility controlling
       * @type {Object}
       */
      openx: config.openx,
      /**
       * The function that should be called when debugging changes
       * @type {Function}
       */
      debug_callback: config.debug,
    };

    // Add the handlers
    if (!igloo.browser.mobile) {
      config.iframe.addEventListener('mouseover', mouse_handler.bind(undefined, igloo_units, config.name, true), false);
      config.iframe.addEventListener('mouseout', mouse_handler.bind(undefined, igloo_units, config.name, false), false);
    }

    // Call the debug callback with the initial value
    igloo_units[config.name].debug_callback();

    clearInterval(interval_id);
    interval_id = setInterval(timer_main, 100);

    return igloo_units[config.name];
  } // --> add_unit

  /**
   * Remove the specified unit from the timer protocols
   * @param  {String}  name The key that the unit is assoicated with
   * @return {Boolean}      The success of the function
   */
  function remove_unit(name) {
    log.trace('remove_unit', arguments);

    // Check for existence first
    if (typeof igloo_units[name] !== 'undefined') {
      return delete (igloo_units[name]);
    }

    return false;
  } // --> remove_unit

  /**
   * Start the timer for a new ad
   * @param  {String}  name                   The name of the unit to start the
   *                                          timer for
   * @param  {Number}  timing                 The number of ms to display the ad
   *                                          for
   * @param  {Boolean} do_visibile_adjustment Whether to allow MRC adjustments
   */
  function start_new_ad(name, timing, do_visibile_adjustment) {
    log.trace('start_new_ad', arguments);

    // Check for a valid ad unit
    if (typeof igloo_units[name] === 'undefined') {
      return false;
    }

    // Get the current unit
    var current_unit = igloo_units[name];

    // Add to the count if the ad was MRC adjusted
    if (current_unit.visibility.was_adjusted) {
      current_unit.visibility.visible_count++;
    } else {
      current_unit.visibility.visible_count = 0;
    }

    // Reset the variables
    current_unit.visibility.do_visibile_adjustment = do_visibile_adjustment;
    current_unit.visibility.was_adjusted = false;
    current_unit.visibility.was_logged = false;
    current_unit.bid_requested = false;

    // Set the timing variables
    current_unit.visibility.visible_start = null;
    current_unit.timing.length = timing;
    current_unit.timing.started = (new Date()).getTime();
  } // --> start_new_ad

  /**
   * Add a function to be run every 100ms
   * @param  {Function} timed_function The function to add to the queue
   */
  function register_function(timed_function) {
    log.trace('register_function', arguments);

    // Add to the registered function array
    registered_functions.push(timed_function);
  } // --> register_function

  // Add the needed functions
  registered_functions.push(
    url_check,
    debug_check,
    check_visibility.bind(undefined, igloo_units),
    update_unit_timers.bind(undefined, igloo_units)
  );

  // Set the interval for the main timer
  interval_id = setInterval(timer_main, 100);

  return {
    // Needed for external calls
    addUnit: add_unit,
    removeUnit: remove_unit,
    registerFunction: register_function,
    startNewAd: start_new_ad,

    // Exported for testing
    igloo_units: igloo_units,
    update_unit_timers: update_unit_timers,
    should_unit_pause: should_unit_pause,
    pause_timer: pause_timer,
    get_pause_message: get_pause_message,
    restart_timer: restart_timer,
    update_visibility_marker: update_visibility_marker,
    get_noadjust_reason: get_noadjust_reason,
    mouse_handler: mouse_handler,
  };
}; // --> module.exports
